import React, { useEffect, useState } from "react";
import { CFormInput, CFormLabel, CFormSelect } from "@coreui/react";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type { ExchangeEnvironment } from "@src/types";
import type { ApiAuthField, ApiInfisicalAuthConfig } from "@tradingdroid/frontends-types";

interface Props {
  exchangeName: string;
  exchangeEnvironment: ExchangeEnvironment;
  exchangeType: "cex" | "dex" | "custom";
  quoteCurrency?: string;
  onEnvironmentChange: (env: ExchangeEnvironment) => void;
  onKeyPathChange: (keyPath: string) => void;
  onSecretPathChange: (secretPath: string) => void;
  onPasswordPathChange?: (passwordPath: string) => void;
  disabled?: boolean;
}

// Fallback labels when API hasn't responded yet (pre-fetch state)
const FALLBACK_LABELS: Record<string, ApiAuthField[]> = {
  cex: [
    { name: "api_key", label: "API Key", required: true, placeholder: "" },
    { name: "api_secret", label: "API Secret", required: true, placeholder: "" },
  ],
  dex: [
    { name: "api_key", label: "Wallet Address", required: true, placeholder: "" },
    { name: "api_secret", label: "Private Key", required: true, placeholder: "" },
  ],
  custom: [
    { name: "api_key", label: "API Key", required: true, placeholder: "" },
    { name: "api_secret", label: "Login", required: true, placeholder: "" },
    { name: "password", label: "Password", required: true, placeholder: "" },
  ],
};

// Helper to format balance for display in dropdown
const formatBalance = (keyConfig: ApiInfisicalAuthConfig): string => {
  if (keyConfig.balance_error) {
    return ` - ${keyConfig.balance_error}`;
  }
  if (keyConfig.spot_balance === null && keyConfig.futures_balance === null) {
    return ""; // Balance not fetched
  }

  const parts: string[] = [];
  const currency = keyConfig.quote_currency || "USDT";

  if (keyConfig.spot_balance !== null && keyConfig.spot_balance > 0) {
    const formatted =
      keyConfig.spot_balance >= 1000
        ? `${(keyConfig.spot_balance / 1000).toFixed(1)}k`
        : keyConfig.spot_balance.toFixed(0);
    parts.push(`S: ${formatted}`);
  }

  if (keyConfig.futures_balance !== null && keyConfig.futures_balance > 0) {
    const formatted =
      keyConfig.futures_balance >= 1000
        ? `${(keyConfig.futures_balance / 1000).toFixed(1)}k`
        : keyConfig.futures_balance.toFixed(0);
    parts.push(`F: ${formatted}`);
  }

  if (parts.length === 0) return "";
  return ` --- ${parts.join(" - ")} ${currency}`;
};

export const ExecutionConfigAuth: React.FC<Props> = ({
  exchangeName,
  exchangeEnvironment,
  exchangeType,
  quoteCurrency,
  onEnvironmentChange,
  onKeyPathChange,
  onSecretPathChange,
  onPasswordPathChange,
  disabled = false,
}) => {
  const { showToast } = useToastActions();
  const [availableKeys, setAvailableKeys] = useState<ApiInfisicalAuthConfig[]>([]);
  const [authFields, setAuthFields] = useState<ApiAuthField[]>(
    FALLBACK_LABELS[exchangeType] || FALLBACK_LABELS.cex,
  );
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string>("");
  const [keyPath, setKeyPath] = useState("");
  const [secretPath, setSecretPath] = useState("");
  const [passwordPath, setPasswordPath] = useState("");

  // Derived from API-driven auth_fields (positional: [0]=key, [1]=secret, [2]=password)
  const keyField = authFields[0] ?? null;
  const secretField = authFields[1] ?? null;
  const passwordField = authFields.length > 2 ? authFields[2] : null;
  const pairLabel = keyField?.label || "API Key";

  // Fetch available Infisical keys when exchange, environment, AND quote currency are available
  useEffect(() => {
    // Clear inputs when exchange or environment changes
    setSelectedKeyId("");
    setKeyPath("");
    setSecretPath("");
    setPasswordPath("");
    onKeyPathChange("");
    onSecretPathChange("");
    onPasswordPathChange?.("");
    setAvailableKeys([]);
    // Reset to fallback labels until API responds
    setAuthFields(FALLBACK_LABELS[exchangeType] || FALLBACK_LABELS.cex);

    // Require all three: exchange, environment, and quoteCurrency (from selected instrument)
    if (!exchangeName || !quoteCurrency) return;

    setIsLoadingKeys(true);

    const fetchKeys = async () => {
      try {
        const response = await tdApi.getUserExchangeAuth(exchangeName, exchangeEnvironment, {
          fetch_balances: "true",
          quote_currency: quoteCurrency,
        });

        const keys = response?.items || [];
        setAvailableKeys(keys);

        // Use API-driven auth field labels if available
        if (response?.auth_fields?.length) {
          setAuthFields(response.auth_fields);
        }
      } catch (error) {
        console.error("Failed to fetch Infisical keys:", error);
        setAvailableKeys([]);
        showToast({
          message: "Failed to fetch Infisical keys",
          type: "danger",
        });
      } finally {
        setIsLoadingKeys(false);
      }
    };

    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeName, exchangeEnvironment, quoteCurrency]);

  const handleKeyPathChange = (value: string) => {
    setKeyPath(value);
    onKeyPathChange(value);
  };

  const handleSecretPathChange = (value: string) => {
    setSecretPath(value);
    onSecretPathChange(value);
  };

  const handlePasswordPathChange = (value: string) => {
    setPasswordPath(value);
    onPasswordPathChange?.(value);
  };

  const handleKeySelection = (keyConfig: ApiInfisicalAuthConfig) => {
    setSelectedKeyId(keyConfig.id);
    setKeyPath(keyConfig.key_path);
    setSecretPath(keyConfig.secret_path);
    onKeyPathChange(keyConfig.key_path);
    onSecretPathChange(keyConfig.secret_path);
    // Auto-fill password path if available
    const pwPath = keyConfig.password_path ?? "";
    setPasswordPath(pwPath);
    onPasswordPathChange?.(pwPath);
  };

  const getDropdownMessage = () => {
    if (!exchangeName) {
      return "Select an exchange first";
    }
    if (!quoteCurrency) {
      return "Select an instrument first to get balance";
    }
    if (isLoadingKeys) {
      return "Loading available keys...";
    }
    if (availableKeys.length === 0) {
      return `No ${pairLabel.toLowerCase()} pairs found for ${exchangeName} (${exchangeEnvironment})`;
    }
    return `${availableKeys.length} ${pairLabel.toLowerCase()}-pairs found. Choose one.`;
  };

  return (
    <div className="ExecutionConfigAuth">
      <div className="d-flex gap-2 mb-3">
        <div style={{ width: "20%" }}>
          <CFormLabel>Environment</CFormLabel>
          <CFormSelect
            value={exchangeEnvironment}
            onChange={(e) => onEnvironmentChange(e.target.value as ExchangeEnvironment)}
            disabled={disabled}
          >
            <option value="qa">QA</option>
            <option value="prod">PROD</option>
          </CFormSelect>
        </div>
        <div style={{ width: "80%" }}>
          <CFormLabel>Select {pairLabel} Pair</CFormLabel>
          <CFormSelect
            value={selectedKeyId}
            onChange={(e) => {
              const selectedKey = availableKeys.find((key) => key.id === e.target.value);
              if (selectedKey) {
                handleKeySelection(selectedKey);
              }
            }}
            disabled={disabled || isLoadingKeys}
          >
            <option value="">{getDropdownMessage()}</option>
            {availableKeys.map((keyConfig) => (
              <option
                key={keyConfig.id}
                value={keyConfig.id}
                title={keyConfig.balance_error || undefined}
                disabled={!!keyConfig.balance_error}
              >
                {keyConfig.display_name}
                {formatBalance(keyConfig)}
              </option>
            ))}
          </CFormSelect>
          <small className="text-muted">Auto-fills Infisical paths below</small>
        </div>
      </div>

      <div className="mb-3">
        <CFormLabel>Infisical {keyField.label} Path</CFormLabel>
        <CFormInput
          type="text"
          value={keyPath}
          onChange={(e) => handleKeyPathChange(e.target.value.toUpperCase())}
          disabled={disabled}
          placeholder={keyField.placeholder}
        />
      </div>

      <div className="mb-3">
        <CFormLabel>Infisical {secretField?.label || "Secret"} Path</CFormLabel>
        <CFormInput
          type="text"
          value={secretPath}
          onChange={(e) => handleSecretPathChange(e.target.value.toUpperCase())}
          disabled={disabled}
          placeholder={secretField?.placeholder}
        />
      </div>

      {passwordField && (
        <div className="mb-3">
          <CFormLabel>Infisical {passwordField.label} Path</CFormLabel>
          <CFormInput
            type="text"
            value={passwordPath}
            onChange={(e) => handlePasswordPathChange(e.target.value.toUpperCase())}
            disabled={disabled}
            placeholder={passwordField.placeholder}
          />
        </div>
      )}
    </div>
  );
};
